/** Viewer-facing PTO cohort toggles for the calendar tile menu. */
export type CalendarPeopleGroup =
  | 'team'
  | 'direct_reports'
  | 'subscribed'
  | 'gratitude'
  | 'reporting_chain'
  | 'direct_plus_one';

export const CALENDAR_PEOPLE_GROUP_ORDER: CalendarPeopleGroup[] = [
  'team',
  'direct_reports',
  'subscribed',
  'gratitude',
  'reporting_chain',
  'direct_plus_one',
];

const REASON_TO_GROUP: Record<string, CalendarPeopleGroup> = {
  manager: 'team',
  peers: 'team',
  team_people: 'team',
  direct_reports: 'direct_reports',
  subscribed: 'subscribed',
  gratitude_sent: 'gratitude',
  gratitude_received: 'gratitude',
  reporting_chain: 'reporting_chain',
  direct_plus_one: 'direct_plus_one',
};

/** Whether each group is on when the viewer has no saved override. */
export const CALENDAR_PEOPLE_GROUP_DEFAULTS: Record<CalendarPeopleGroup, boolean> = {
  team: true,
  direct_reports: true,
  subscribed: true,
  gratitude: true,
  reporting_chain: false,
  direct_plus_one: false,
};

/** Map a server relationship key onto a menu group. */
export const reasonToCalendarPeopleGroup = (reason?: string): CalendarPeopleGroup | null =>
  reason ? (REASON_TO_GROUP[reason] ?? null) : null;

export type CalendarPeopleFilterState = Partial<Record<CalendarPeopleGroup, boolean>>;

const SELF_CALENDAR_REASON = 'self';

/** Resolve on/off; unset keys fall back to {@link CALENDAR_PEOPLE_GROUP_DEFAULTS}. */
export const isCalendarPeopleGroupEnabled = (
  filter: CalendarPeopleFilterState,
  group: CalendarPeopleGroup
): boolean => {
  if (group in filter) {
    return filter[group] === true;
  }
  return CALENDAR_PEOPLE_GROUP_DEFAULTS[group];
};

export const toggleCalendarPeopleGroup = (
  filter: CalendarPeopleFilterState,
  group: CalendarPeopleGroup
): CalendarPeopleFilterState => {
  if (isCalendarPeopleGroupEnabled(filter, group)) {
    return {...filter, [group]: false};
  }
  if (CALENDAR_PEOPLE_GROUP_DEFAULTS[group]) {
    const next = {...filter};
    delete next[group];
    return next;
  }
  return {...filter, [group]: true};
};

/** True when every available group matches its default on/off state. */
export const isPeopleFilterAtDefaults = (
  filter: CalendarPeopleFilterState,
  available: CalendarPeopleGroup[]
): boolean =>
  available.every(
    (group) => isCalendarPeopleGroupEnabled(filter, group) === CALENDAR_PEOPLE_GROUP_DEFAULTS[group]
  );

/**
 * Which menu groups appear in the viewer's calendar data (empty cohorts are
 * hidden). Callers pass every event type they show — PTO, birthdays, and
 * anniversaries — so the toggle reflects everyone on the calendar, not just PTO.
 */
export const availableCalendarPeopleGroups = (
  people: Array<{reason?: string}>
): CalendarPeopleGroup[] => {
  const seen = new Set<CalendarPeopleGroup>();
  for (const person of people) {
    const group = reasonToCalendarPeopleGroup(person.reason);
    if (group) {
      seen.add(group);
    }
  }
  return CALENDAR_PEOPLE_GROUP_ORDER.filter((group) => seen.has(group));
};

/** Filter any calendar rows (PTO, birthdays, anniversaries) by enabled cohort groups. */
export const filterByCalendarPeopleGroups = <T extends {reason?: string}>(
  people: T[],
  filter: CalendarPeopleFilterState,
  available: CalendarPeopleGroup[]
): T[] => {
  const enabled = new Set(available.filter((group) => isCalendarPeopleGroupEnabled(filter, group)));
  return people.filter((person) => {
    if (person.reason === SELF_CALENDAR_REASON) {
      return true;
    }
    const group = reasonToCalendarPeopleGroup(person.reason);
    return group != null && enabled.has(group);
  });
};
